import { GENERATION_TOOL, type GenerationTool } from 'src/data/generationTool';
import type { ShortFormSection } from 'src/data/shortForm';

/**
 * The content types `ShortFormContentTypeEnum` accepts. `shortFormToolsForContentType` is the only
 * caller that needs them narrowed this way — see `toShortFormContentType`.
 */
export const SHORT_FORM_CONTENT_TYPE = {
  // ------------ Other ------------
  generalImage: 'general_image',
  generalText: 'general_text',

  // ------------ Social ------------
  facebookPost: 'facebook_post',
  instagramPost: 'instagram_caption',
  linkedinPost: 'linkedin_post',
  twitterPost: 'twitter_post',
  youtubeVideo: 'youtube_video',

  // ------------ Ads ------------
  facebookAd: 'facebook_ad',
  linkedinAd: 'linkedin_ad',
  twitterAd: 'twitter_ad',
  googlePerformanceMaxAd: 'google_performance_max_ad',
  googleSearchAd: 'google_search_ad',
  pinterestAd: 'pinterest_ad',
  outbrainAd: 'outbrain_ad',
  taboolaAd: 'taboola_ad',
  linkedinConversationAd: 'linkedin_conversation_ad',
  linkedinMessageAd: 'linkedin_message_ad',
  linkedinLeadGenFormAd: 'linkedin_lead_gen_form_ad',
  linkedinDocumentAd: 'linkedin_document_ad',
  linkedinTextAd: 'linkedin_text_ad',
  linkedinVideoAd: 'linkedin_video_ad',

  // ------------ Email ------------
  email: 'email',
  emailSubjectLine: 'email_subject_line',

  // ------------ Website ------------
  webpageHeadline: 'webpage_headline',
  webpageSubHeadline: 'webpage_sub_headline',
  blogHeadline: 'blog_headline',
  seoMetaDescription: 'seo_meta_description',

  // ------------ Ecommerce ------------
  amazonProductListing: 'amazon_product_listing',
  walmartProductListing: 'walmart_product_listing',
  generalProductListing: 'general_product_listing',
  productReview: 'product_review',
  sms: 'sms',
} as const;
export type ShortFormContentType =
  (typeof SHORT_FORM_CONTENT_TYPE)[keyof typeof SHORT_FORM_CONTENT_TYPE];

/** The content types the long-form editor still offers. */
const ACTIVE_LONG_FORM_CONTENT_TYPE = {
  generalText: 'general_text',
  // TODO: merge with `general_image`. The two name the same content type and differ only because
  // `LongFormContentTypeEnum` spells it `image` while `ShortFormContentTypeEnum` spells it
  // `general_image`; the backend still labels image asset groups with this one.
  image: 'image',
  webpage: 'web_page',
  blogPost: 'blog_post',
  email: 'email',
  whitePaper: 'white_paper',
  researchReport: 'research_report',
} as const;

/**
 * Content types `LongFormContentTypeEnum` still accepts although the long-form editor no longer
 * offers them. Every one of them is a short-form value too, so they are members here and carry
 * their title and tool from the table below like any other type.
 */
const LEGACY_LONG_FORM_CONTENT_TYPE = {
  instagramPost: 'instagram_caption',
  facebookPost: 'facebook_post',
  linkedinPost: 'linkedin_post',
  twitterPost: 'twitter_post',
  facebookAd: 'facebook_ad',
  linkedinAd: 'linkedin_ad',
  linkedinConversationAd: 'linkedin_conversation_ad',
  linkedinMessageAd: 'linkedin_message_ad',
  linkedinLeadGenFormAd: 'linkedin_lead_gen_form_ad',
  linkedinDocumentAd: 'linkedin_document_ad',
  linkedinTextAd: 'linkedin_text_ad',
  linkedinVideoAd: 'linkedin_video_ad',
  twitterAd: 'twitter_ad',
  googlePerformanceMaxAd: 'google_performance_max_ad',
  googleSearchAd: 'google_search_ad',
  pinterestAd: 'pinterest_ad',
} as const;

/** The content types `LongFormContentTypeEnum` accepts — see `toLongFormContentType`. */
export const LONG_FORM_CONTENT_TYPE = {
  ...ACTIVE_LONG_FORM_CONTENT_TYPE,
  ...LEGACY_LONG_FORM_CONTENT_TYPE,
} as const;
export type LongFormContentType =
  (typeof LONG_FORM_CONTENT_TYPE)[keyof typeof LONG_FORM_CONTENT_TYPE];

/** Every content type the editor deals with, long form and short form alike. */
export const IMPROVER_CONTENT_TYPE = {
  ...SHORT_FORM_CONTENT_TYPE,
  ...LONG_FORM_CONTENT_TYPE,
} as const;
export type ImproverContentType = ShortFormContentType | LongFormContentType;

const SHORT_FORM_CONTENT_TYPES: ImproverContentType[] = Object.values(SHORT_FORM_CONTENT_TYPE);
const LONG_FORM_CONTENT_TYPES: ImproverContentType[] = Object.values(LONG_FORM_CONTENT_TYPE);

/**
 * The two backend enums overlap without either containing the other, and a value missing from an
 * enum makes GraphQL reject the whole request — so a caller bound to one of them has to ask whether
 * the type it holds is a member.
 */
export const toShortFormContentType = (
  contentType: ImproverContentType
): ShortFormContentType | undefined =>
  SHORT_FORM_CONTENT_TYPES.includes(contentType)
    ? (contentType as ShortFormContentType)
    : undefined;

export const toLongFormContentType = (
  contentType: ImproverContentType
): LongFormContentType | undefined =>
  LONG_FORM_CONTENT_TYPES.includes(contentType) ? (contentType as LongFormContentType) : undefined;

// The backend's list, not ours: these are the content types an improve returns as one long-form
// card, and the same ones whose parts `extractOutlineFromContent` discovers from the content.
const LONG_FORM_CARD_CONTENT_TYPES: ImproverContentType[] = [
  IMPROVER_CONTENT_TYPE.generalText,
  IMPROVER_CONTENT_TYPE.email,
  IMPROVER_CONTENT_TYPE.webpage,
  IMPROVER_CONTENT_TYPE.blogPost,
  IMPROVER_CONTENT_TYPE.whitePaper,
  IMPROVER_CONTENT_TYPE.researchReport,
];

/** An improve of this content type comes back as a single long-form card. */
export const improverContentTypeProducesLongFormCard = (
  contentType: ImproverContentType
): boolean => LONG_FORM_CARD_CONTENT_TYPES.includes(contentType);

/**
 * The parts of this content type are discovered by the backend from the pasted content, rather than
 * being the fixed slots `shortFormToolsForContentType` hands back.
 */
export const improverContentTypeExtractsOutline = (contentType: ImproverContentType): boolean =>
  LONG_FORM_CARD_CONTENT_TYPES.includes(contentType);

/** `localizeContent` accepts exactly the types a long-form card is made of. */
export const improverContentTypeSupportsLocalization = (
  contentType: ImproverContentType
): boolean => LONG_FORM_CARD_CONTENT_TYPES.includes(contentType);

export const improverContentTypeHasScreenshotInput = (
  contentType: ImproverContentType
): boolean => {
  const types: ImproverContentType[] = [
    SHORT_FORM_CONTENT_TYPE.facebookPost,
    SHORT_FORM_CONTENT_TYPE.instagramPost,
    SHORT_FORM_CONTENT_TYPE.linkedinPost,
    SHORT_FORM_CONTENT_TYPE.twitterPost,
    SHORT_FORM_CONTENT_TYPE.facebookAd,
    SHORT_FORM_CONTENT_TYPE.linkedinAd,
    SHORT_FORM_CONTENT_TYPE.twitterAd,
    SHORT_FORM_CONTENT_TYPE.pinterestAd,
    SHORT_FORM_CONTENT_TYPE.outbrainAd,
    SHORT_FORM_CONTENT_TYPE.taboolaAd,
    SHORT_FORM_CONTENT_TYPE.linkedinTextAd,
  ];
  return types.includes(contentType);
};

export const improverContentTypeHasCsvInput = (contentType: ImproverContentType): boolean => {
  const types: ImproverContentType[] = [
    SHORT_FORM_CONTENT_TYPE.googlePerformanceMaxAd,
    SHORT_FORM_CONTENT_TYPE.googleSearchAd,
  ];
  return types.includes(contentType);
};

export const improverContentTypeHasSectionNumbering = (
  contentType: ImproverContentType
): boolean => {
  const types: ImproverContentType[] = [
    SHORT_FORM_CONTENT_TYPE.googlePerformanceMaxAd,
    SHORT_FORM_CONTENT_TYPE.googleSearchAd,
  ];
  return types.includes(contentType);
};

interface ImproverContentTypeProps {
  title: string;
  generationTool: GenerationTool;
  withSingleAsset: boolean;
}

const IMPROVER_CONTENT_TYPE_PROPS: Record<ImproverContentType, ImproverContentTypeProps> = {
  // ------------ Other ------------
  [SHORT_FORM_CONTENT_TYPE.generalImage]: {
    title: 'Image',
    generationTool: GENERATION_TOOL.imageImprove,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.generalText]: {
    title: 'Text',
    generationTool: GENERATION_TOOL.basicContentImprover,
    withSingleAsset: false,
  },

  // ------------ Social ------------
  [SHORT_FORM_CONTENT_TYPE.facebookPost]: {
    title: 'Facebook Post',
    generationTool: GENERATION_TOOL.facebookPost,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.instagramPost]: {
    title: 'Instagram Post',
    generationTool: GENERATION_TOOL.instagramCaption,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.linkedinPost]: {
    title: 'LinkedIn Post',
    generationTool: GENERATION_TOOL.linkedinPost,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.twitterPost]: {
    title: 'X Post',
    generationTool: GENERATION_TOOL.tweet,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.youtubeVideo]: {
    title: 'Youtube Video',
    generationTool: GENERATION_TOOL.youtubeMixturePost,
    withSingleAsset: false,
  },

  // ------------ Ads ------------
  [SHORT_FORM_CONTENT_TYPE.facebookAd]: {
    title: 'Meta Image ad',
    generationTool: GENERATION_TOOL.facebookMixtureAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.linkedinAd]: {
    title: 'LinkedIn Single Image ad',
    generationTool: GENERATION_TOOL.linkedinMixtureAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.linkedinConversationAd]: {
    title: 'LinkedIn Conversation ad',
    generationTool: GENERATION_TOOL.linkedinConversationAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.linkedinMessageAd]: {
    title: 'LinkedIn Message ad',
    generationTool: GENERATION_TOOL.linkedinMessageAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.linkedinLeadGenFormAd]: {
    title: 'LinkedIn Lead Gen Form ad',
    generationTool: GENERATION_TOOL.linkedinLeadGenFormAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.linkedinDocumentAd]: {
    title: 'LinkedIn Document ad',
    generationTool: GENERATION_TOOL.linkedinDocumentAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.linkedinTextAd]: {
    title: 'LinkedIn Text ad',
    generationTool: GENERATION_TOOL.linkedinTextAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.linkedinVideoAd]: {
    title: 'LinkedIn Video ad',
    generationTool: GENERATION_TOOL.linkedinVideoAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.googlePerformanceMaxAd]: {
    title: 'Google Performance Max ad',
    generationTool: GENERATION_TOOL.googlePerformanceMaxAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.googleSearchAd]: {
    title: 'Google Search ad',
    generationTool: GENERATION_TOOL.googleRSA,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.twitterAd]: {
    title: 'X Image Ad',
    generationTool: GENERATION_TOOL.twitterMixtureAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.pinterestAd]: {
    title: 'Pinterest Image Ad',
    generationTool: GENERATION_TOOL.pinterestMixtureAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.outbrainAd]: {
    title: 'Outbrain ad',
    generationTool: GENERATION_TOOL.outbrainAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.taboolaAd]: {
    title: 'Taboola ad',
    generationTool: GENERATION_TOOL.taboolaAd,
    withSingleAsset: false,
  },

  // ------------ Email ------------
  [SHORT_FORM_CONTENT_TYPE.email]: {
    title: 'Email',
    generationTool: GENERATION_TOOL.emailMixture,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.emailSubjectLine]: {
    title: 'Email - Subject line',
    generationTool: GENERATION_TOOL.emailSubjectLine,
    withSingleAsset: true,
  },

  // ------------ Website ------------
  [SHORT_FORM_CONTENT_TYPE.webpageHeadline]: {
    title: 'Webpage - Headline',
    generationTool: GENERATION_TOOL.landingHeadline,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.webpageSubHeadline]: {
    title: 'Webpage - Subheadline',
    generationTool: GENERATION_TOOL.landingSubheader,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.blogHeadline]: {
    title: 'Blog - Headline',
    generationTool: GENERATION_TOOL.articleBlogHeadline,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.seoMetaDescription]: {
    title: 'SEO Meta Description',
    generationTool: GENERATION_TOOL.landingSEOMetadata,
    withSingleAsset: false,
  },

  // ------------ Ecommerce ------------
  [SHORT_FORM_CONTENT_TYPE.amazonProductListing]: {
    title: 'Amazon product listing',
    generationTool: GENERATION_TOOL.amazonProductListing,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.walmartProductListing]: {
    title: 'Walmart product listing',
    generationTool: GENERATION_TOOL.walmartProductListing,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.generalProductListing]: {
    title: 'Product Listing',
    generationTool: GENERATION_TOOL.generalProductListing,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.productReview]: {
    title: 'Product Review',
    generationTool: GENERATION_TOOL.basicProductReview,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.sms]: {
    title: 'SMS',
    generationTool: GENERATION_TOOL.smsPromotional,
    withSingleAsset: true,
  },

  // ------------ Long form ------------
  // A long-form content type has no tool of its own. The backend improves it by content type, and
  // finds a tool for each part it extracts. The tool below only supplies the panel's props, so it
  // must not reach the user as a name — `getImproverContentTypeTitle` is the label.
  [LONG_FORM_CONTENT_TYPE.webpage]: {
    title: 'Webpage',
    generationTool: GENERATION_TOOL.basicLandingPage, // TODO: use some non-deprecated tool
    withSingleAsset: false,
  },
  [LONG_FORM_CONTENT_TYPE.blogPost]: {
    title: 'Blog / Article',
    generationTool: GENERATION_TOOL.blogDescription,
    withSingleAsset: false,
  },
  [LONG_FORM_CONTENT_TYPE.whitePaper]: {
    title: 'White paper',
    generationTool: GENERATION_TOOL.basicPerfectHeadline,
    withSingleAsset: false,
  },
  [LONG_FORM_CONTENT_TYPE.researchReport]: {
    title: 'Research report',
    generationTool: GENERATION_TOOL.basicPerfectHeadline,
    withSingleAsset: false,
  },
  [LONG_FORM_CONTENT_TYPE.image]: {
    title: 'Image',
    generationTool: GENERATION_TOOL.imageByStyle,
    withSingleAsset: false,
  },
};

export const getImproverContentTypeTitle = (contentType: ImproverContentType): string =>
  IMPROVER_CONTENT_TYPE_PROPS[contentType]?.title || contentType;

export const getImproverContentTypeTool = (contentType: ImproverContentType): GenerationTool => {
  return IMPROVER_CONTENT_TYPE_PROPS[contentType]?.generationTool;
};

export const isSingleAssetRewriteContentType = (contentType: ImproverContentType): boolean => {
  return contentType ? Boolean(IMPROVER_CONTENT_TYPE_PROPS[contentType]?.withSingleAsset) : false;
};

const SINGLE_SECTION_CONTENT_TYPES: ImproverContentType[] = [
  IMPROVER_CONTENT_TYPE.webpage,
  IMPROVER_CONTENT_TYPE.blogPost,
  IMPROVER_CONTENT_TYPE.whitePaper,
  IMPROVER_CONTENT_TYPE.researchReport,
  IMPROVER_CONTENT_TYPE.generalImage,
  IMPROVER_CONTENT_TYPE.generalText,
  IMPROVER_CONTENT_TYPE.emailSubjectLine,
  IMPROVER_CONTENT_TYPE.blogHeadline,
  IMPROVER_CONTENT_TYPE.seoMetaDescription,
  IMPROVER_CONTENT_TYPE.amazonProductListing,
  IMPROVER_CONTENT_TYPE.walmartProductListing,
  IMPROVER_CONTENT_TYPE.generalProductListing,
  IMPROVER_CONTENT_TYPE.productReview,
  IMPROVER_CONTENT_TYPE.sms,
];

/** The whole input is one section, so it is edited without a name or a checkbox of its own. */
export const improverContentTypeHasSingleSection = (contentType: ImproverContentType): boolean =>
  SINGLE_SECTION_CONTENT_TYPES.includes(contentType);

/** Whether this section of this content type is edited as markup rather than as plain text. */
export const improverSectionSupportsHtml = (
  contentType: ImproverContentType,
  section: Pick<ShortFormSection, 'generationTool'>
): boolean => {
  if (contentType === IMPROVER_CONTENT_TYPE.generalText) {
    return true;
  }

  if (
    contentType === IMPROVER_CONTENT_TYPE.email &&
    section.generationTool === GENERATION_TOOL.emailBody
  ) {
    return true;
  }

  // A long-form content type has no fixed sections: its one section is the document itself, text
  // and images together.
  return !toShortFormContentType(contentType);
};

/**
 * Where the content can be brought in from instead of being typed. Both lists are the long-form
 * Content Improver's, which is where these content types come from.
 */
export const improverContentTypeSupportsFileImport = (
  contentType: ImproverContentType
): boolean => {
  const typesWithoutFileImport: ImproverContentType[] = [
    IMPROVER_CONTENT_TYPE.generalText,
    IMPROVER_CONTENT_TYPE.blogPost,
    IMPROVER_CONTENT_TYPE.email,
  ];
  return (
    improverContentTypeProducesLongFormCard(contentType) &&
    !typesWithoutFileImport.includes(contentType)
  );
};

export const improverContentTypeSupportsUrlImport = (contentType: ImproverContentType): boolean => {
  const typesWithoutUrlImport: ImproverContentType[] = [
    IMPROVER_CONTENT_TYPE.whitePaper,
    IMPROVER_CONTENT_TYPE.researchReport,
  ];
  return (
    improverContentTypeSupportsFileImport(contentType) &&
    !typesWithoutUrlImport.includes(contentType)
  );
};
